import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import AddPositionsToPortfolioDialog from '@/components/investments/AddPositionsToPortfolioDialog';
import type { WealthicaPosition } from '@/providers/wealthica2/types';
import type { GenericPosition } from '@/components/investments/dialog/types';

export type AddPositionsPayload = {
  positions: (WealthicaPosition | GenericPosition)[];
  onSuccess?: () => void;
  navigateOnZeroAdded?: boolean;
  onClose?: () => void;
};

interface AddPositionsContextValue {
  open: (payload: AddPositionsPayload) => void;
  close: () => void;
}

const AddPositionsContext = createContext<AddPositionsContextValue | undefined>(undefined);

export function AddPositionsProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState<AddPositionsPayload | null>(null);

  const open = useCallback((p: AddPositionsPayload) => {
    setPayload(p);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // allow dialog to unmount before clearing payload
    setTimeout(() => setPayload(null), 0);
  }, []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <AddPositionsContext.Provider value={value}>
      {children}
      {payload && (
        <AddPositionsToPortfolioDialog
          open={isOpen}
          onOpenChange={(o) => { if (!o) close(); }}
          positions={payload.positions}
          onSuccess={payload.onSuccess}
          navigateOnZeroAdded={payload.navigateOnZeroAdded}
          onClose={() => { payload.onClose?.(); close(); }}
        />
      )}
    </AddPositionsContext.Provider>
  );
}

export function useAddPositionsContext() {
  const ctx = useContext(AddPositionsContext);
  if (!ctx) throw new Error('useAddPositionsContext must be used within AddPositionsProvider');
  return ctx;
}
